import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { catchError, map } from 'rxjs/operators';
import { Observable, throwError, empty, forkJoin } from 'rxjs';

import { ListaResultset } from './../../../core/list/lista-resultset.interface';
import { IItemInventarioDTO } from './../item-inventario/item-inventario-resumido.interface';
import { StatusRetirada } from './../../../core/enums/status-retirada.enum';
import { Solicitacao } from '../solicitacao/solicitacao.model';
import { IItemProdutoRetirada } from '../item-produto-retirada/item-produto-retirada.model';
import { DefaultService } from './../../../core/framework/default-service';
import { ErrorHandlerService } from './../../../core/framework/error-handler.service';
import { ListaComponent } from './../../../core/list/lista.component';
import { IItemInventarioRetirada } from './model/item-inventario-retirada.interface';
import { Retirada } from './model/retirada.model';
import { BaixaDTO } from '../baixa-de-produto-v2/model/baixa.dto';
import { ItemEstoqueRetirada } from './model/item-estoque-retirada.interface';


@Injectable({
  providedIn: 'root'
})
export class RetiradaService extends DefaultService<Retirada> {

  static URL = '/api/saida/v1/retiradas';

  constructor(
      public httpClient: HttpClient,
      public errorHandler: ErrorHandlerService
  ) {
      super(
        httpClient,
        errorHandler,
        RetiradaService.URL
      );
  }

  public listarItensRetirada(statusRetirada: StatusRetirada, idRetirada: number, listComponent?: ListaComponent): Observable<ListaResultset<IItemProdutoRetirada>> {
      if ( statusRetirada === StatusRetirada.RETIRADA || statusRetirada === StatusRetirada.BAIXADA ) {
          return this.listarItensProdutoDoEstoqueEInventarioRetirada(idRetirada)
                     .pipe(
                       catchError ( (err: any) => {
                          throwError(this.errorHandler.handle(err));
                          if (listComponent) {
                              listComponent.setLoading(false);
                              listComponent.hasError = true;
                          }
                          return empty();
                        })
                     );
      }
      else {
          return this.listarItensProdutoRetirada(idRetirada, listComponent);
      }
  }

  private listarItensProdutoRetirada(idRetirada: number, listComponent?: ListaComponent): Observable<ListaResultset<IItemProdutoRetirada>> {
      return this.httpClient.get<ListaResultset<IItemProdutoRetirada>>(`${RetiradaService.URL}/${idRetirada}/itensProdutoRetirada`)
                            .pipe(
                                catchError ( (err: any) => {
                                    throwError(this.errorHandler.handle(err));
                                    if (listComponent) {
                                        listComponent.setLoading(false);
                                        listComponent.hasError = true;
                                    }
                                    return empty();
                                })
      );
  }

  private listarItensProdutoDoEstoqueEInventarioRetirada(idRetirada: number): Observable<ListaResultset<IItemProdutoRetirada>> {
    const $lotes = [];

    /* PRODUTOS NO ESTOQUE */
    $lotes.push(
      this.listarItensEstoqueRetirada(idRetirada)
          .pipe(
            map((resultset: ListaResultset<ItemEstoqueRetirada[]>) => {
              const ierLista: any = resultset.content;
              const itemProdutoLista: IItemProdutoRetirada[] = [];

              ierLista
                .forEach((ier: ItemEstoqueRetirada) => {
                  const itemProduto: IItemProdutoRetirada = {
                    idItemInventario: null,
                    inventario:       null,
                    quantidade:       ier.quantidadeRetirada,
                    lote:             ier.itemEstoqueRetiradaPK.lote,
                    itemProdutoRetiradaPK: {
                      produto: ier.itemEstoqueRetiradaPK.lote.produto
                    }
                  }

                  itemProdutoLista.push(itemProduto);
                })

              return itemProdutoLista;
            })
          )
    );

    /* PRODUTOS NOS INVENTARIOS */
    $lotes.push(
      this.listarItensInventarioRetirada(idRetirada)
          .pipe(
            map((resultset: ListaResultset<IItemInventarioRetirada[]>) => {
              const iirLista: any = resultset.content;
              const itemProdutoLista: IItemProdutoRetirada[] = [];

              iirLista
                .forEach((iir: IItemInventarioRetirada) => {
                  const itemProduto: IItemProdutoRetirada = {
                    idItemInventario: iir.itemInventarioRetiradaPK.itemInventario.id,
                    inventario:       iir.itemInventarioRetiradaPK.itemInventario.inventario,
                    quantidade:       iir.quantidadeRetirada,
                    lote:             iir.itemInventarioRetiradaPK.itemInventario.lote,
                    itemProdutoRetiradaPK: {
                      produto: iir.itemInventarioRetiradaPK.itemInventario.lote.produto
                    }
                  }

                  itemProdutoLista.push(itemProduto);
                });

                return itemProdutoLista;
            })
          )
    );

    return forkJoin($lotes)
            .pipe(
              map((resultado: IItemProdutoRetirada[][]) => {
                const itensProdutos: IItemProdutoRetirada[] = [];
                resultado.forEach(o => {
                  o.forEach((itemProduto: IItemProdutoRetirada) => {
                    itensProdutos.push( itemProduto );
                  })
                })

                return new ListaResultset<IItemProdutoRetirada>({
                  content: itensProdutos,
                  size: itensProdutos.length
                });
              })
            )
  }

  private listarItensInventarioRetirada(idRetirada: number, listComponent?: ListaComponent): Observable<ListaResultset<IItemInventarioRetirada[]>> {
      return this.httpClient.get<ListaResultset<IItemInventarioRetirada[]>>(`${RetiradaService.URL}/${idRetirada}/itensInventarioRetirada`)
      .pipe(
          catchError ( (err: any) => {
              throwError(this.errorHandler.handle(err));
              if (listComponent) {
                  listComponent.setLoading(false);
                  listComponent.hasError = true;
              }
              return empty();
          })
      );
  }

  private listarItensEstoqueRetirada(idRetirada: number, listComponent?: ListaComponent): Observable<ListaResultset<ItemEstoqueRetirada[]>> {
    return this.httpClient.get<ListaResultset<ItemEstoqueRetirada[]>>(`${RetiradaService.URL}/${idRetirada}/itensEstoqueRetirada`)
    .pipe(
        catchError ( (err: any) => {
            throwError(this.errorHandler.handle(err));
            if (listComponent) {
                listComponent.setLoading(false);
                listComponent.hasError = true;
            }
            return empty();
        })
    );
  }

  solicitar(solicitacao: Solicitacao): Observable<Retirada> {
      return this.httpClient.post<Retirada>(`${RetiradaService.URL}/solicitar`, solicitacao, this.httpOptions)
                            .pipe (
                              catchError( (err: any) => throwError(this.errorHandler.handle(err)))
                            );
  }

  atualizarSolicitacao(solicitacao: Solicitacao, idRetiradaAlteracao?: number): Observable<Retirada> {
      return this.httpClient.put<Retirada>(`${RetiradaService.URL}/solicitar/${idRetiradaAlteracao}`, solicitacao, this.httpOptions)
                            .pipe (
                              catchError( (err: any) => throwError(this.errorHandler.handle(err)))
                            );
  }

  aprovar(idRetirada: number): Observable<Retirada> {
      return this.httpClient.put<Retirada>(`${RetiradaService.URL}/${idRetirada}/aprovar`, this.httpOptions)
                            .pipe (
                              catchError( (err: any) => throwError(this.errorHandler.handle(err)))
                            );
  }

  reprovar(idRetirada: number): Observable<Retirada> {
      return this.httpClient.put<Retirada>(`${RetiradaService.URL}/${idRetirada}/reprovar`, this.httpOptions)
                            .pipe (
                              catchError( (err: any) => throwError(this.errorHandler.handle(err)))
                            );
  }

  retirar(idRetirada: number, itensInventarioResumido: IItemInventarioDTO[]): Observable<Retirada> {
      return this.httpClient.put<Retirada>(`${RetiradaService.URL}/${idRetirada}/retirar`, itensInventarioResumido, this.httpOptions)
                            .pipe (
                              catchError( (err: any) => throwError(this.errorHandler.handle(err)))
                            );
  }

  cancelar(idRetirada: number): Observable<Retirada> {
    return this.httpClient.put<Retirada>(`${RetiradaService.URL}/${idRetirada}/cancelar`, this.httpOptions)
                          .pipe (
                            catchError( (err: any) => throwError(this.errorHandler.handle(err)))
                          );
  }

  baixar(baixaDTO: BaixaDTO): Observable<Retirada> {
    return this.httpClient.post<Retirada>(`${RetiradaService.URL}/baixar`, baixaDTO, this.httpOptions)
                          .pipe (
                            catchError( (err: any) => throwError(this.errorHandler.handle(err)))
                          );
  }

}
